// Weekly-plan shopping aggregation — pure functions, no UI, no IO.
//
// Walks a plan's filled slots, finds what the pantry can't cover, and
// returns rows ready for setShoppingList (the useSyncedList persister).
// Book/expanded slots run the real pairing engine; unexpanded concept
// slots do a canonical presence check on their estimated ingredients.
//
// Aggregation dedupes by canonical (falling back to normalized name)
// and unit-sums with convertWithBridge; when two amounts genuinely
// can't convert into each other we keep two rows rather than lie.
//
// Re-runnable: pass the current shopping list and anything already on
// it (unpurchased, same canonical) is skipped — so SYNC after a swap
// only inserts deltas.

import { findIngredient, inferCanonicalFromName } from "../data/ingredients";
import { pairRecipeIngredients } from "./recipePairing";
import { convertWithBridge } from "./unitConvert";
import { parseAmountString } from "./nutrition";

const normName = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").replace(/\s+/g, " ").trim();

function newId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// One "need": { canonicalId|null, name, amount|null, unit }
function addNeed(agg, need) {
  const key = need.canonicalId || `name:${normName(need.name)}`;
  const existing = agg.get(key);
  if (!existing) {
    agg.set(key, { ...need, extras: [] });
    return;
  }
  // Merge amounts when both are numeric. Same unit sums directly;
  // different units try the density-aware bridge into the FIRST-seen
  // unit. Unconvertible → ride as an extra line (rendered as its own
  // row) instead of a made-up sum.
  if (need.amount == null || existing.amount == null) {
    existing.amount = existing.amount ?? need.amount;
    existing.unit = existing.unit || need.unit;
    return;
  }
  if ((existing.unit || "") === (need.unit || "")) {
    existing.amount += need.amount;
    return;
  }
  const canonical = need.canonicalId ? findIngredient(need.canonicalId) : null;
  const conv = canonical
    ? convertWithBridge({ amount: need.amount, unit: need.unit }, existing.unit, canonical)
    : { ok: false };
  if (conv?.ok && Number.isFinite(conv.value)) {
    existing.amount += conv.value;
  } else {
    existing.extras.push({ amount: need.amount, unit: need.unit });
  }
}

// Recipe ingredient → { amount, unit } in the recipe's own terms,
// scaled by servings ratio. qty (structured) wins; else parse the
// display string; else amount-less (null).
function ingredientQty(ing, scale) {
  if (ing?.qty && Number.isFinite(Number(ing.qty.amount))) {
    return { amount: Number(ing.qty.amount) * scale, unit: ing.qty.unit || "" };
  }
  const canonical = ing?.ingredientId ? findIngredient(ing.ingredientId) : null;
  if (typeof ing?.amount === "string" && canonical) {
    const parsed = parseAmountString(ing.amount, canonical);
    if (parsed && Number.isFinite(parsed.amount)) {
      return { amount: parsed.amount * scale, unit: parsed.unit || "" };
    }
  }
  const n = typeof ing?.amount === "number" ? ing.amount : parseFloat(ing?.amount);
  if (Number.isFinite(n)) return { amount: n * scale, unit: ing?.unit || "" };
  return { amount: null, unit: "" };
}

/**
 * @param {object} opts
 * @param {Array}    opts.slots         — the plan's filled slot objects
 * @param {function} opts.resolveRecipe — (slot) => full recipe for book /
 *                                        expanded slots, or null (concepts)
 * @param {Array}    opts.pantry        — usePantry rows
 * @param {number}   opts.householdServings
 * @param {Array}    opts.existingList  — current shopping list (camelCase rows)
 * @returns {Array} rows for setShoppingList (fresh ids, source "meal-plan")
 */
export function computeWeekShoppingItems({
  slots = [],
  resolveRecipe,
  pantry = [],
  householdServings = 2,
  existingList = [],
} = {}) {
  const agg = new Map();

  // Canonical identities the pantry already covers (same construction
  // as aiContext's pantryCanonicals — presence check for concepts).
  const pantryCanonicals = new Set();
  for (const p of pantry) {
    if (p?.canonicalId) pantryCanonicals.add(p.canonicalId);
    if (p?.ingredientId) pantryCanonicals.add(p.ingredientId);
  }

  for (const slot of slots) {
    if (!slot || slot.error || !slot.source) continue;

    const recipe = typeof resolveRecipe === "function" ? resolveRecipe(slot) : null;
    if (recipe?.ingredients?.length) {
      // Real recipe — run the pairing engine; buy only true misses
      // (wrong-state and authored-substitute rows are coverable from
      // the pantry, matching the "+ SHOP shows on missing" precedent).
      const scale = householdServings / Math.max(1, Number(recipe.serves) || 1);
      const pairings = pairRecipeIngredients(recipe.ingredients, pantry);
      pairings.forEach((pairing, idx) => {
        if (pairing?.status !== "missing") return;
        const ing = recipe.ingredients[idx];
        if (!ing) return;
        const canonicalId =
          pairing.resolvedCanonicalId || ing.ingredientId ||
          inferCanonicalFromName(ing.item || ing.name) || null;
        const name = findIngredient(canonicalId)?.name || ing.item || ing.name;
        if (!name) return;
        addNeed(agg, { canonicalId, name, ...ingredientQty(ing, scale) });
      });
    } else if (slot.source === "new" && slot.concept?.estIngredients) {
      // Unexpanded concept — presence check against pantry canonicals.
      for (const est of slot.concept.estIngredients) {
        if (!est) continue;
        const canonicalId =
          (est.canonicalId && findIngredient(est.canonicalId) ? est.canonicalId : null) ||
          inferCanonicalFromName(est.name) || null;
        if (canonicalId && pantryCanonicals.has(canonicalId)) continue;
        const name = findIngredient(canonicalId)?.name || est.name;
        if (!name) continue;
        addNeed(agg, {
          canonicalId,
          name,
          amount: Number.isFinite(Number(est.amount)) ? Number(est.amount) : null,
          unit: est.unit || "",
        });
      }
    }
  }

  // Skip anything already awaiting purchase on the list.
  const listedCanonicals = new Set();
  const listedNames = new Set();
  for (const item of existingList) {
    if (item?.purchasedAt) continue;
    if (item?.ingredientId) listedCanonicals.add(item.ingredientId);
    if (item?.name) listedNames.add(normName(item.name));
  }

  const rows = [];
  for (const need of agg.values()) {
    if (need.canonicalId && listedCanonicals.has(need.canonicalId)) continue;
    if (!need.canonicalId && listedNames.has(normName(need.name))) continue;
    const canonical = need.canonicalId ? findIngredient(need.canonicalId) : null;
    const base = {
      name: canonical?.name || need.name,
      emoji: canonical?.emoji || "🥫",
      ingredientId: need.canonicalId,
      category: canonical?.category || "pantry",
      source: "meal-plan",
    };
    rows.push({
      id: newId(),
      ...base,
      amount: need.amount != null ? Math.round(need.amount * 100) / 100 : 1,
      unit: need.unit || "",
    });
    for (const extra of need.extras) {
      rows.push({
        id: newId(),
        ...base,
        amount: Math.round(extra.amount * 100) / 100,
        unit: extra.unit || "",
      });
    }
  }
  return rows;
}
